import React from 'react';
import { useTranslate, useNotify } from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
} from '@mui/material';
import { ContentCopy, VpnKey } from '@mui/icons-material';

interface EnrollmentCodeDialogProps {
    open: boolean;
    code: string;
    expiresAt: string | null;
    onClose: () => void;
}

export const EnrollmentCodeDialog: React.FC<EnrollmentCodeDialogProps> = ({ open, code, expiresAt, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        notify(translate('resources.kiosk-devices.enrollment.copied'), { type: 'info' });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VpnKey sx={{ color: '#00C853' }} />
                    {translate('resources.kiosk-devices.enrollment.title')}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {translate('resources.kiosk-devices.enrollment.body')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.5rem', fontWeight: 600, letterSpacing: 1 }}>
                            {code}
                        </Typography>
                        <IconButton onClick={handleCopy} size="small">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Box>
                    {expiresAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {translate('resources.kiosk-devices.enrollment.expires', { when: new Date(expiresAt).toLocaleString() })}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCopy} startIcon={<ContentCopy />} sx={{ textTransform: 'none' }}>
                    {translate('resources.kiosk-devices.enrollment.copy')}
                </Button>
                <Button onClick={onClose} variant="contained" sx={{ backgroundColor: '#00C853', '&:hover': { backgroundColor: '#00A843' }, textTransform: 'none', fontWeight: 600 }}>
                    {translate('resources.kiosk-devices.enrollment.done')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
